import { Badge, Group, SimpleGrid, Stack, Text } from "@mantine/core";

/** The run's quality as the operators need it (M6.5): coverage, unseen, dust, position health,
 * timing and PTP, window blockage; the flags and the grade. */

type Q = { grade?: string; coverage_frac?: number; unobserved_frac?: number; flags?: string[]; metrics?: Record<string, number> };

const pct = (v: number | undefined) => (v === undefined || !Number.isFinite(v) ? "—" : `${(100 * v).toFixed(1)} %`);
const num = (v: number | undefined, unit: string, digits = 0) =>
  v === undefined || !Number.isFinite(v) ? "—" : `${v.toFixed(digits)} ${unit}`;

export const GRADE_COLOUR: Record<string, string> = { good: "teal", degraded: "yellow", poor: "red" };

export function qualityItems(q: Q): { label: string; value: string; warn: boolean }[] {
  const m = q.metrics ?? {};
  const flags = new Set(q.flags ?? []);
  const kept = m["filter_kept"] ?? 0;
  const dust = m["filter_dust"] ?? 0;
  const dustFrac = kept + dust > 0 ? dust / (kept + dust) : undefined;
  return [
    { label: "Coverage", value: pct(q.coverage_frac), warn: (q.coverage_frac ?? 1) < 0.9 },
    { label: "Unseen", value: pct(q.unobserved_frac), warn: (q.unobserved_frac ?? 0) > 0.05 },
    { label: "Dust", value: pct(dustFrac), warn: (dustFrac ?? 0) > 0.05 },
    {
      label: "Position gaps",
      value: num(m["pos_gap_max_ms"], "ms max"),
      warn: (m["pose_gaps_over_limit"] ?? 0) > 0 || flags.has("pose_invalid_columns"),
    },
    {
      label: "Time offset",
      value:
        m["timing_offset_ms"] === undefined
          ? "—"
          : `${m["timing_offset_ms"].toFixed(1)} ± ${(m["timing_sigma_ms"] ?? Number.NaN).toFixed(1)} ms`,
      warn: flags.has("timing_unverified") || flags.has("timing_drift"),
    },
    { label: "PTP", value: flags.has("timing_degraded") ? "not locked" : "locked", warn: flags.has("timing_degraded") },
    {
      label: "Window",
      value: flags.has("window_blocked") ? "blocked" : "clear",
      warn: flags.has("window_blocked"),
    },
  ];
}

export function QualityPanel({ quality }: { quality: Q }) {
  const items = qualityItems(quality);
  return (
    <Stack gap="xs" data-testid="quality-panel">
      <Group gap="xs">
        <Text size="sm" fw={600}>
          Quality
        </Text>
        <Badge color={GRADE_COLOUR[quality.grade ?? ""] ?? "gray"}>{quality.grade ?? "—"}</Badge>
      </Group>
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
        {items.map((it) => (
          <div key={it.label}>
            <Text size="xs" c="dimmed">
              {it.label}
            </Text>
            <Text size="sm" c={it.warn ? "orange.8" : undefined} fw={it.warn ? 600 : undefined}>
              {it.value}
            </Text>
          </div>
        ))}
      </SimpleGrid>
      {(quality.flags ?? []).length > 0 && (
        <Group gap={4}>
          {(quality.flags ?? []).map((f) => (
            <Badge key={f} size="xs" variant="light" color="gray" tt="none">
              {f}
            </Badge>
          ))}
        </Group>
      )}
    </Stack>
  );
}
